import React from 'react'
import {EventModel, getEventImageUrl} from 'event/models/Event'
import {LocalLink} from 'i18n/LocalLink'
import 'moment/locale/bg'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import EventSeatIcon from '@mui/icons-material/EventSeat'
import styled from 'styled-components'
import {publicImageUrl} from 'organization/ImageSelector'
import defaultEventImage from 'images/defaultEventImage.webp'
import {eventRoutes} from 'event/EventRoutes'
import {formatEventTicketPriceLabel} from 'event/ticketPrices'
import {formatUserDay} from 'utils/lib/date'
import {useI18n} from 'i18n/I18nContext'

export const EventListItem = (props: {event: EventModel; index: number}) => {
  const {t} = useI18n()
  const {event, index} = props

  const routes = eventRoutes(event)
  const eventImage = getEventImageUrl(event, 1)

  const background = eventImage ? publicImageUrl(eventImage) : defaultEventImage

  const eventPrice = formatEventTicketPriceLabel(event, t)

  return (
    <div
      key={index}
      className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-xs-12"
    >
      <ItemHolder
        className="event__venue-card"
        style={{
          borderBottom: '2px solid #f7426f',
        }}
      >
        <ImageHolder $background={background} $darken={!!event.ended_at} />

        <div className="event__venue-thumb ">
          <div className="event__tag-post">
            {!event.ended_at && (
              <div className="event__tag-item" style={{fontSize: 11}}>
                <span style={{background: '#ffc15b'}}>
                  <TrendingUpIcon fontSize={'small'} /> {t('eventRow.trending')}
                </span>
              </div>
            )}
            <div className="event__tag-item" style={{fontSize: 11}}>
              <span>
                <EventSeatIcon fontSize={'small'} />
                {event.ended_at
                  ? t('eventRow.ended')
                  : t('eventRow.seatsAvailable').replace(
                      '{n}',
                      String(event.available_seats),
                    )}
              </span>
            </div>
          </div>
        </div>

        <NameHolder>
          {event.ended_at && <div style={{color: '#ffffff'}}>{event.name}</div>}
          {!event.ended_at && (
            <NameLink to={routes.root}>{event.name}</NameLink>
          )}
        </NameHolder>
        <ItemInternalHolder>
          <Description>{event.short_description}</Description>

          <div className="event__venue-meta">
            {!event.ended_at && (
              <DateHolder className="news__meta-status">
                <div>
                  <span className="mr-5">
                    <i className="flaticon-calendar-1"></i>
                  </span>
                  <span>
                    {event.date
                      ? formatUserDay(event.date)
                      : t('eventRow.noInfo')}
                  </span>
                </div>
                <div>
                  <span className="mr-5">
                    <i className="flaticon-clock"></i>
                  </span>
                  <span>{event.time ? event.time : t('eventRow.noInfo')} </span>
                </div>
              </DateHolder>
            )}

            {event.ended_at && (
              <DateHolder className="news__meta-status">
                <div>
                  <span>{t('eventRow.endedBadge')}</span>
                </div>
              </DateHolder>
            )}
          </div>

          <div
            className="news__meta-status"
            style={{justifyContent: 'space-between', alignItems: 'center'}}
          >
            <PriceHolder>{eventPrice}</PriceHolder>
            <LabelHolder className="pricing__item-label">
              {event.ticket_name}
            </LabelHolder>
          </div>

          {event.id < 30000 && (
            <EndedButton type="button" className={'input__btn w-100 mt-30'}>
              {t('eventRow.ended')}
            </EndedButton>
          )}
          {event.id >= 30000 && (
            <LocalLink to={routes.root} className={'input__btn w-100 mt-30'}>
              {t('eventRow.viewEvent')}
            </LocalLink>
          )}
        </ItemInternalHolder>
      </ItemHolder>
    </div>
  )
}

const ItemHolder = styled.div`
  padding: 0;
  margin: 30px 0;
  background: #f5f5f5;
`

const ItemInternalHolder = styled.div`
  padding: 15px;
`

const ImageHolder = styled.div<{
  $background: string | undefined
  $darken?: boolean
}>`
  position: relative;
  background-image: url(${(props) => props.$background});
  width: 100%;
  height: 200px;
  background-position: center;
  background-size: cover;
  border-radius: 6px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  ${(props) =>
    props.$darken &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 6px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  `}
`

const NameHolder = styled.h3`
  font-weight: bold;
  background: var(--clr-gradient-14);
  padding: 15px;
  font-size: 1rem;
`

const NameLink = styled(LocalLink)`
  color: #ffffff;
  display: inline-block;
`

const Description = styled.div`
  display: inline-block;
  width: 100%;
  height: 55px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #4f4f4f;
`

const DateHolder = styled.div`
  display: flex;
  width: 100%;
`

const PriceHolder = styled.span`
  background: linear-gradient(91deg, #f7426f 5.15%, #f87a58 99.88%);
  background-clip: text;
  font-size: 2rem !important;
  font-weight: bold;
  -webkit-text-fill-color: transparent;
`

const EndedButton = styled.button`
  background: var(--clr-text-primary);
  color: var(--clr-text-secondary);

  &:hover {
    color: var(--clr-text-secondary);
  }
`

const LabelHolder = styled.div``
